import React from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle2, Loader2, FileSignature, Shield, AlertTriangle } from "lucide-react";
import { API } from "../lib/api";

/**
 * Public digital contract signing page.
 * URL: /underskriv/:token
 *
 * Flow:
 *  1. GET /api/sign/contract/:token → preview applicant data + contract
 *  2. User types their full name + accepts terms
 *  3. POST /api/sign/contract/:token → captures typed_name + IP + timestamp
 */
export default function ContractSignPage() {
  const { token } = useParams();
  const [loading, setLoading] = React.useState(true);
  const [data, setData] = React.useState(null);
  const [err, setErr] = React.useState(null);
  const [typedName, setTypedName] = React.useState("");
  const [accept, setAccept] = React.useState(false);
  const [busy, setBusy] = React.useState(false);
  const [signed, setSigned] = React.useState(null);

  React.useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await fetch(`${API}/sign/contract/${token}`);
        if (!res.ok) {
          const j = await res.json().catch(() => ({}));
          throw new Error(j.detail || `Fejl ${res.status}`);
        }
        const d = await res.json();
        if (!alive) return;
        setData(d);
        if (d.status === "signed") setSigned({ signed_at: d.signed_at, typed_name: d.typed_name });
        setTypedName(d.applicant?.full_name || "");
      } catch (e) {
        if (alive) setErr(e.message);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [token]);

  // Detect MitID success callback
  React.useEffect(() => {
    if (typeof window === "undefined") return;
    const params = new URLSearchParams(window.location.search);
    if (params.get("status") === "mitid-success") {
      toast.success("Tak — du har underskrevet med MitID!");
    }
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (typedName.trim().length < 2) {
      toast.error("Skriv venligst dit fulde navn"); return;
    }
    if (!accept) {
      toast.error("Du skal acceptere kontrakten for at underskrive"); return;
    }
    setBusy(true);
    try {
      const res = await fetch(`${API}/sign/contract/${token}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ typed_name: typedName.trim(), accept: true }),
      });
      const j = await res.json();
      if (!res.ok) throw new Error(j.detail || `Fejl ${res.status}`);
      setSigned({ signed_at: j.signed_at, typed_name: typedName.trim() });
      toast.success("Tak — din underskrift er registreret!");
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-brand-bg2">
        <Loader2 className="animate-spin text-brand-red" size={32} />
      </div>
    );
  }

  if (err) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-brand-bg2 px-4">
        <div className="bg-white border border-brand p-10 max-w-md text-center" data-testid="sign-error">
          <AlertTriangle size={40} className="text-brand-red mx-auto" />
          <h1 className="font-display font-bold text-2xl text-brand-dark mt-4">Linket virker ikke</h1>
          <p className="text-brand-text2 mt-2 text-sm">{err}</p>
          <p className="text-brand-text2 mt-4 text-xs">Kontakt <a href="mailto:job@emakurer.dk" className="underline">job@emakurer.dk</a> hvis du har brug for et nyt link.</p>
        </div>
      </div>
    );
  }

  const a = data?.applicant || {};
  const fullAddress = [a.address, a.zip_code, a.city].filter(Boolean).join(", ");

  if (signed) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-brand-bg2 px-4 py-12">
        <div className="bg-white border border-brand p-10 max-w-xl text-center" data-testid="sign-success">
          <div className="w-16 h-16 bg-emerald-100 text-emerald-700 mx-auto flex items-center justify-center rounded-full">
            <CheckCircle2 size={36} />
          </div>
          <h1 className="font-display font-bold text-3xl text-brand-dark mt-6">Tak! Du er underskrevet</h1>
          <p className="text-brand-text2 mt-3">
            Vi har registreret din digitale underskrift kl.{" "}
            <strong className="text-brand-dark">{new Date(signed.signed_at).toLocaleString("da-DK")}</strong>.
          </p>
          <p className="text-brand-text2 mt-4 text-sm">
            Du modtager besked på email <strong>{a.email}</strong> så snart vi er klar til at starte dig op.
          </p>
          <p className="text-brand-text2 mt-6 text-xs">
            Underskrift registreret som: <span className="italic">{signed.typed_name}</span>
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-brand-bg2 px-4 py-10">
      <div className="max-w-3xl mx-auto" data-testid="sign-page">
        <div className="bg-brand-dark text-white p-8 sm:p-10">
          <div className="kicker text-white/70">EMA Kurer ApS · Digital underskrift</div>
          <h1 className="font-display font-bold text-3xl sm:text-4xl tracking-tight mt-3">
            Underskriv din ansættelseskontrakt
          </h1>
          <p className="text-white/80 mt-4 text-sm sm:text-base leading-relaxed">
            Hej <strong>{a.full_name}</strong>. Læs kontrakten igennem og bekræft din underskrift nedenfor.
            Det tager under 1 minut.
          </p>
        </div>

        <div className="bg-white border border-brand p-7 sm:p-10 mt-0">
          <div className="kicker">Dine oplysninger</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-2 mt-3 text-sm">
            <div><span className="text-brand-text2">Navn:</span> <strong>{a.full_name}</strong></div>
            <div><span className="text-brand-text2">Email:</span> <strong>{a.email}</strong></div>
            <div><span className="text-brand-text2">Telefon:</span> <strong>{a.phone}</strong></div>
            <div><span className="text-brand-text2">Adresse:</span> <strong>{fullAddress || "—"}</strong></div>
            {a.license_plate && <div><span className="text-brand-text2">Nummerplade:</span> <strong className="font-mono">{a.license_plate}</strong></div>}
            <div><span className="text-brand-text2">Statsborgerskab:</span> <strong>{a.is_danish_citizen ? "Dansk" : (a.nationality || "Andet")}</strong></div>
          </div>

          <div className="kicker mt-8">Kontrakt — kort sammendrag</div>
          <div className="mt-3 space-y-3 text-sm text-brand-dark leading-relaxed">
            <p><strong>Arbejdsgiver:</strong> EMA Kurer ApS, CVR 46465717, Ullehøj 35, 4140 Borup. Direktør: Sylwia Landowska. Kontakt: Firat Cagler.</p>
            <p><strong>§3 Ansættelsesform.</strong> Du ansættes som <strong>akkordlønnet tilkaldeansat kurérchauffør</strong> — du aflønnes pr. rute via Burds akkord-tid-algoritme. Ingen garanteret minimumstimetal og <strong>ingen garanteret minimumsindtjening</strong>.</p>
            <p><strong>§6 Arbejdstider.</strong> Mandag–Fredag: 18:00–22:30 · Søndag: 17:00–22:30 · Lørdag: Lukket. Forventeligt 3-4 vagter ugentligt.</p>
            <p><strong>§7 Vagtplan.</strong> Accepterede vagter er bindende. Mød til tiden ved rutestart.</p>
            <p><strong>§8 Akkord-løn.</strong> <strong>150 DKK pr. time af planlagt rutetid</strong> (akkord-tid beregnet af Burds algoritme) + <strong>2,00 DKK/km skattefrit</strong>. Ingen ekstra betaling for ventetid mellem ruter. Feriepenge 12,5 % til FerieKonto. Løn via Salary senest den 20. i efterfølgende måned.</p>
            <p><strong>§9 Akkord-tid.</strong> Ruter aflønnes i <strong>akkord-tid</strong> — den planlagte rutetid som Burds algoritme beregner. Planlagt 2-timers rute = 2 timer betaling, uanset om ruten afsluttes tidligere eller senere. Burd Driver-app'en viser altid hvad du aflønnes for + rutens start/slut. Se burd.dk for Burds koncept.</p>
            <p><strong>§11 Bil og udstyr.</strong> Du anvender egen bil — nummerplade <strong className="font-mono">{a.license_plate || "(oplyses senere)"}</strong>. Mikrobiler accepteres ikke. Smartphone, GPS, oplader og telefonholder påkrævet.</p>
            <p><strong>§13 Sygdom.</strong> Meld senest kl. 11:00 på sygedagen. Ingen løn under sygdom (da aflønning er akkord).</p>
            <p><strong>§14 Sikkerhedsvest.</strong> Skal bæres under arbejdet. 50 DKK fratrækkes hvis ikke returneret.</p>
            <p><strong>§17 Opsigelse.</strong> 3 mdr. prøvetid. 7 dages opsigelse i prøvetid · 14 dages efter prøvetid.</p>
            <p><strong>§18 Lovgivning.</strong> Ansættelsen følger dansk ret og gældende ferielov.</p>
            <p className="text-xs text-brand-text2 italic mt-4">Dette er et resumé. Den fulde, juridisk bindende ansættelseskontrakt fremsendes som PDF efter underskrift — satser, vilkår og opsigelse er identiske.</p>
          </div>

          <form onSubmit={submit} className="mt-8 border-t border-brand pt-6">
            <div>
              <label className="label-flat">Dit fulde navn (digital underskrift) <span className="text-brand-red">*</span></label>
              <input
                className="input-flat font-display text-lg italic"
                value={typedName}
                onChange={(e) => setTypedName(e.target.value)}
                data-testid="sign-typed-name"
                placeholder="Skriv dit fulde navn her"
                required
              />
              <div className="text-xs text-brand-text2 mt-1">
                Ved at indtaste dit navn og acceptere nedenfor underskriver du elektronisk.
                Vi gemmer dato/tid og IP-adresse som bevis for underskriften.
              </div>
            </div>

            <label className="flex items-start gap-3 mt-5 cursor-pointer bg-brand-bg2 border border-brand p-4">
              <input
                type="checkbox"
                checked={accept}
                onChange={() => setAccept((v) => !v)}
                data-testid="sign-accept"
                className="mt-1"
              />
              <span className="text-sm text-brand-dark leading-relaxed">
                Jeg har læst og accepterer ansættelseskontrakten med EMA Kurer ApS som beskrevet ovenfor.
              </span>
            </label>

            <button
              type="submit"
              className="btn-primary mt-6 w-full sm:w-auto justify-center"
              disabled={busy || typedName.trim().length < 2 || !accept}
              data-testid="sign-submit"
            >
              {busy ? <><Loader2 className="animate-spin" size={16} /> Underskriver...</> : <><FileSignature size={16} /> Underskriv med navn</>}
            </button>
          </form>

          <div className="my-6 flex items-center gap-3">
            <div className="flex-1 h-px bg-brand"></div>
            <span className="text-xs uppercase tracking-wider text-brand-text2 font-semibold">Eller anbefalet</span>
            <div className="flex-1 h-px bg-brand"></div>
          </div>

          <a
            href={`${API}/sign/contract/${token}/mitid/initiate`}
            className="block bg-[#0060e6] hover:bg-[#0052cc] text-white text-center font-semibold py-4 px-6 transition-colors"
            data-testid="sign-mitid"
          >
            <div className="flex items-center justify-center gap-3">
              <svg width="28" height="28" viewBox="0 0 32 32" fill="currentColor" aria-hidden="true">
                <rect x="2" y="6" width="28" height="20" rx="3" fill="#fff" />
                <text x="16" y="20" textAnchor="middle" fontSize="9" fontWeight="700" fill="#0060e6" fontFamily="Arial">MitID</text>
              </svg>
              Underskriv med MitID
            </div>
            <div className="text-xs font-normal text-white/85 mt-1">
              Stærkere bevis — kræver dit MitID-app eller code app
            </div>
          </a>

          <div className="mt-8 pt-5 border-t border-brand flex items-start gap-3 text-xs text-brand-text2">
            <Shield size={16} className="text-brand-text2 mt-0.5 flex-shrink-0" />
            <div>
              Sikker digital underskrift jf. eIDAS-forordningen (artikel 3, stk. 10) — "simpel elektronisk
              underskrift". Vi gemmer dit indtastede navn, IP-adresse og tidsstempel som bevis.
              EMA Kurer ApS · CVR 46465717 · kontakt@emakurer.dk
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
